import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { RATIONAL_META, type RationalParams } from './rational.model';

/** Rational params with index signature for JsxParamStore compatibility */
type RationalParamsIndexed = RationalParams & { [key: string]: number };

const EPSILON = 1e-12;

/**
 * Creates rational functions: a / (x - h) + k
 *
 * Parameters:
 * - a: vertical scale
 * - h: horizontal shift (vertical asymptote)
 * - k: vertical shift (horizontal asymptote)
 */
export class RationalCreator extends BaseFunctionCreator<RationalParamsIndexed> {
  readonly functionType = 'rational';
  readonly defaultParams: RationalParamsIndexed = { ...RATIONAL_META.defaultParams };
  readonly availableFeatures = RATIONAL_META.availableFeatures;
  readonly handleConfigs = RATIONAL_META.handleConfigs;
  override readonly computedValueKeys = ['verticalAsymptote', 'horizontalAsymptote', 'yIntercept'] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as RationalParamsIndexed;

    const colorName = (display?.curve?.color ?? 'purple') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: RATIONAL_META.defaultRanges.a },
      h: { value: initialParams.h, range: RATIONAL_META.defaultRanges.h },
      k: { value: initialParams.k, range: RATIONAL_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      const denom = x - h;
      if (Math.abs(denom) < EPSILON) {
        return NaN;
      }
      return a / denom + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // f(x) = a / (x-h) + k
    // f'(x) = -a / (x-h)²
    // f''(x) = 2a / (x-h)³
    // f^(n)(x) = (-1)^n * n! * a / (x-h)^(n+1)
    const getDerivative = (order = 1): ((x: number) => number) => {
      return (x: number) => {
        const a = paramStore.getValue('a');
        const h = paramStore.getValue('h');
        const denom = x - h;
        if (Math.abs(denom) < EPSILON) {
          return NaN;
        }
        let factorial = 1;
        for (let i = 2; i <= order; i++) {
          factorial *= i;
        }
        const sign = order % 2 === 0 ? 1 : -1;
        return (sign * factorial * a) / Math.pow(denom, order + 1);
      };
    };

    return {
      id,
      type: 'rational',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as RationalParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      getComputedValues: () => ({
        verticalAsymptote: () => paramStore.getValue('h'),
        horizontalAsymptote: () => paramStore.getValue('k'),
        yIntercept: () => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          const k = paramStore.getValue('k');
          return a / -h + k;
        },
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
